<script setup lang="ts">
import { ref } from 'vue'
import VueCropper from 'vue-cropperjs'
import 'cropperjs/dist/cropper.css'
import { onClickOutside } from '@vueuse/core'
import { useTranslations } from '@/composables/useTranslations.ts'
const { t } = useTranslations()

defineProps<{ open: boolean; imageUrl: string | null }>()
const emit = defineEmits(['close', 'save'])

const modalRef = ref(null)
const cropperRef = ref<InstanceType<typeof VueCropper> | null>(null)
const croppedImage = ref<string | null>(null)

const getCroppedImage = (ev: Event) => {
  if (cropperRef.value) {
    // @ts-expect-error Aleksey please fix it
    const croppedCanvas = cropperRef.value.getCroppedCanvas()
    if (croppedCanvas) {
      const size = Math.min(croppedCanvas.width, croppedCanvas.height)
      const circleCanvas = document.createElement('canvas')
      circleCanvas.width = size
      circleCanvas.height = size

      const ctx = circleCanvas.getContext('2d')
      if (ctx) {
        ctx.beginPath()
        ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2)
        ctx.closePath()
        ctx.clip()
        ctx.drawImage(
          croppedCanvas,
          (croppedCanvas.width - size) / 2,
          (croppedCanvas.height - size) / 2,
          size,
          size,
          0,
          0,
          size,
          size
        )
      }

      circleCanvas.toBlob(function (blob) {
        croppedImage.value = circleCanvas.toDataURL()
        emit('save', circleCanvas.toDataURL(), croppedImage.value)
      })
    }
  }

  ev.preventDefault()
  ev.stopPropagation()
}

onClickOutside(modalRef, () => {
  emit('close')
})
</script>

<template>
  <div v-if="open" class="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-75 z-[1000]">
    <slot name="loader"></slot>
    <div ref="modalRef" class="bg-white rounded-lg p-6 shadow-lg w-96">
      <h2 class="text-lg font-semibold mb-4">{{ t('settings.primary.cropImage') }}</h2>

      <vue-cropper
        v-if="imageUrl"
        ref="cropperRef"
        :src="imageUrl"
        :stencil="false"
        :aspect-ratio="1"
        :view-mode="1"
        class="w-full h-64"
      />

      <div class="flex justify-end gap-2 mt-4">
        <button @click="$emit('close')" class="bg-gray-300 px-4 py-2 rounded">
          {{ t('common.buttons.cancel') }}
        </button>
        <button @click="(ev) => getCroppedImage(ev)" class="bg-indigo-600 text-white px-4 py-2 rounded">
          {{ t('common.buttons.save') }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
::v-deep(.cropper-view-box) {
  border-radius: 50%;
  outline: 2px solid #39f;
  overflow: hidden;
}

::v-deep(.cropper-face) {
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.3) !important;
}

::v-deep(.cropper-dashed) {
  display: none;
}

::v-deep(.cropper-view-box::before),
::v-deep(.cropper-view-box::after) {
  display: none;
}
</style>
